import { COLLISION_CELLS_PER_TILE, foldZones, type FoldedZones, type MapDocument, type ValidationIssue, type WorldDocument } from "daedalus-core";
import { DOOR_COMBAT } from "../zones/doors.ts";
import { ZONE_RULES, ZoneRule } from "../zones/zones.ts";
import { ARRIVAL_POINT } from "./type-names.ts";

/**
 * OdysseyTales' check of the doors that let fighters through (ADR-122 decision 2.5 and mass case 9; its note "how
 * P1.4-T138 is built", item 5): a door whose "In combat" is Let through is an escape from a fight where its arrival
 * point stands on ground sheltered from players (Sanctuary, whose folded zone carries `NoPvp`), or where its
 * destination is a room nobody outside it can follow into (`party`, `solo` or `owner`). Each is an ignorable warning,
 * never a refusal: the builder may mean it. The setting is read as the compile writes it, so a door saved without it
 * refuses and is never warned of. A door whose destination or arrival point is gone is the link checks' to report.
 */

/**
 * The shares a character outside the copy cannot follow into.
 */
const UNFOLLOWABLE: ReadonlySet<string> = new Set(["party", "solo", "owner"]);

/**
 * Whether a map's folded zone at a position in tiles shelters a character from players.
 *
 * @param map The map.
 * @param zones Its folded zones.
 * @param x The column in tiles.
 * @param y The row in tiles.
 * @returns True on a cell whose zone carries `NoPvp`.
 */
function sheltered(map: MapDocument, zones: FoldedZones, x: number, y: number): boolean {
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const column = Math.floor(x * COLLISION_CELLS_PER_TILE);
  const row = Math.floor(y * COLLISION_CELLS_PER_TILE);
  if (column < 0 || row < 0 || column >= width || row >= map.height * COLLISION_CELLS_PER_TILE) {
    return false;
  }
  const held = zones.cells[row * width + column] as number;
  const type = held === 0 ? undefined : zones.types[held - 1];
  return type !== undefined && ((ZONE_RULES[type] ?? 0) & ZoneRule.NoPvp) !== 0;
}

/**
 * The warnings of one map's doors that let fighters through into a Sanctuary or into a room nobody can follow into.
 *
 * @param map The map holding the doors.
 * @param world The world, for each door's destination; each destination is folded once.
 * @returns The warnings, by layer, then by door.
 */
export function doorCombatIssues(map: MapDocument, world: WorldDocument): ValidationIssue[] {
  const folded = new Map<string, FoldedZones>();
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "door") {
      continue;
    }
    for (const door of layer.doors) {
      if (door.fields[DOOR_COMBAT] !== "allow") {
        continue;
      }
      const target = world.maps.find((candidate) => candidate.id === door.to.map);
      const arrival = target?.layers.flatMap((candidate) => (candidate.kind === "entity" ? candidate.entities : [])).find((entity) => entity.id === door.to.arrival && entity.type === ARRIVAL_POINT);
      if (target === undefined || arrival === undefined) {
        continue;
      }
      const where = { mapId: map.id, layerId: layer.id, doorId: door.id, severity: "warning" as const, ignorable: true as const };
      let zones = folded.get(target.id);
      if (zones === undefined) {
        zones = foldZones(target);
        folded.set(target.id, zones);
      }
      if (sheltered(target, zones, arrival.x, arrival.y)) {
        issues.push({ ...where, kind: "door.fighters-into-sanctuary", message: `door ${door.name} lets fighters through into a Sanctuary; set In combat to Refuse, or ignore this when it is meant` });
      }
      if (target.share !== undefined && UNFOLLOWABLE.has(target.share)) {
        issues.push({ ...where, kind: "door.fighters-unfollowable", message: `door ${door.name} lets fighters through into a room nobody can follow them into; set In combat to Refuse, or ignore this when it is meant` });
      }
    }
  }
  return issues;
}
